import { Button } from '@heroui/react';
import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate } from 'react-router-dom';

import { ErrorCard } from '../../../../components/ErrorCard';
import { useGetChatData, useRestrictChatAccess } from '../../hooks';
import { useActiveTabName } from '../../hooks/useActiveTabName';

import {
  EditChatHeader,
  EditChatSkeleton,
  EditChatTabPanels,
  EditChatTabs,
} from './components';
import { DuplicateChatModal } from './components/DuplicateChatModal';
import {
  ChatPageContextProvider,
  DeliverySettingContextProvider,
} from './contexts';

export const EditChat: React.FC = () => {
  const { chatId } = useParams<{ chatId: string }>();
  const navigate = useNavigate();
  // `activeTab` is always a supported tab; stale/unknown hashes (e.g. the
  // retired `#order-guide`, issue #124) resolve to Participants.
  const { rawHash, activeTab } = useActiveTabName();
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);

  const handleChatError = useCallback((err: Error) => {
    // Error will be handled by the hook and displayed in UI
    // eslint-disable-next-line no-console
    console.error('Failed to load chat:', err);
  }, []);

  const { chatData, isLoading, error, refetch } = useGetChatData(chatId || '', {
    onError: handleChatError,
  });

  // Enforce the Puppy/Dogfood-mode boundary on direct chat URLs. No-op while
  // the chat is loading (isPuppy undefined); redirects with an explanation on
  // a mismatch once the chat resolves.
  useRestrictChatAccess(chatData?.isPuppy);

  const pageTitle = chatData
    ? `${chatData.restaurantName} / ${
        chatData.supplierEntity?.name || 'Unknown Distributor'
      } - Choco Admin`
    : 'Edit Chat - Choco Admin';

  // Normalize the URL hash onto the resolved tab. Covers both an absent hash
  // (default to Participants, like the old implementation) and a stale/unknown
  // hash such as the retired `#order-guide` (issue #124), so the address bar
  // and the highlighted tab always agree on a supported tab.
  useEffect(() => {
    if (chatId && rawHash !== activeTab) {
      navigate(`#${activeTab}`, { replace: true });
    }
  }, [rawHash, activeTab, chatId, navigate]);

  // Handle missing chatId
  if (!chatId) {
    return (
      <div className="p-6 min-h-screen bg-background">
        <ErrorCard
          title="Invalid Chat ID"
          message="No chat ID provided in the URL."
          action={{
            label: 'Back to Chats',
            onPress: () => navigate('/chats'),
            color: 'primary',
          }}
        />
      </div>
    );
  }

  // Skeleton mirrors the page structure so a slow chat query reads as
  // progress rather than a hung screen.
  if (isLoading) {
    return <EditChatSkeleton />;
  }

  // Handle error state
  if (error || !chatData) {
    return (
      <div className="p-6 min-h-screen bg-background">
        <ErrorCard
          title="Failed to Load Chat"
          message={error?.message || 'Unable to load chat details.'}
          action={
            <div className="flex gap-3">
              <Button variant="bordered" onPress={() => navigate('/chats')}>
                Back to Chats
              </Button>
              <Button
                color="primary"
                onPress={() => refetch()}
                isLoading={isLoading}
              >
                Try Again
              </Button>
            </div>
          }
        />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <div className="p-6 min-h-screen bg-background">
        <DeliverySettingContextProvider>
          <ChatPageContextProvider initialChatData={chatData}>
            <div className="max-w-7xl mx-auto space-y-6">
              {/* Header Section */}
              <EditChatHeader
                chatData={chatData}
                chatId={chatId}
                onDuplicate={() => setIsDuplicateModalOpen(true)}
              />

              {/* Duplicate Chat Modal */}
              <DuplicateChatModal
                isOpen={isDuplicateModalOpen}
                onClose={() => setIsDuplicateModalOpen(false)}
                chatId={chatId}
                chatData={chatData}
              />

              {/* Tabs Navigation */}
              <EditChatTabs activeTab={activeTab} />

              {/* Tab Content */}
              <EditChatTabPanels
                activeTab={activeTab}
                chatData={chatData}
                chatId={chatId}
              />
            </div>
          </ChatPageContextProvider>
        </DeliverySettingContextProvider>
      </div>
    </>
  );
};
